import { withdrawApis } from "@payping/api";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { Badge, Col, Descriptions, Drawer, Grid, Row, Skeleton } from "@payping/web-components";
import { ComponentProps, Dispatch, FC, SetStateAction } from "react";
import { ensureString } from "@payping/utilities-shared/string";
import { useAsyncRetry } from "react-use";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { getCorrectDate } from "@/utils/getCorrectDate";

interface Props {
  code: string;
  setCode: Dispatch<SetStateAction<string | undefined>>;
}

export const WithdrawDetails: FC<Props> = (props) => {
  const { code, setCode } = props;
  const { t } = useAppTranslation();
  const { md } = Grid.useBreakpoint();
  const { loading, value: details } = useAsyncRetry(() => withdrawApis.withdrawDetails(code), [code]);

  function getWithdrawStatus(param: { isRepaid: boolean | undefined }): {
    color: ComponentProps<typeof Badge>["status"];
    name: string;
  } {
    switch (true) {
      case param.isRepaid:
        return { color: "success", name: t("withdraw.status.isRepaid") };
      default:
        return { color: "processing", name: t("withdraw.status.isNotRepaid") };
    }
  }

  const { name: statusText, color } = getWithdrawStatus({ isRepaid: details?.isRepaid });
  const { string: reqDateFa } = toJalali({
    date: getCorrectDate(details?.reqDate),
    toFormat: dateFormats.ui.jalaliDateTime,
  });
  const { string: repayDateFa } = toJalali({
    date: getCorrectDate(details?.repayDate),
    toFormat: dateFormats.ui.jalaliDateTime,
  });
  const trackingNumber = details?.isPardakhtYar ? details?.trackingNumber : details?.payareferencCode;
  const settlementType = details?.isPardakhtYar
    ? t("report.withdraws.details.isPardakhtyar")
    : t("report.withdraws.details.isNotPardakhtyar");

  return (
    <Drawer
      title={t("report.withdraws.details.title")}
      placement={md ? "left" : "bottom"}
      open={Boolean(code)}
      onClose={() => setCode(undefined)}
      height={"100%"}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions column={1} bordered title={t("report.withdraws.details.title")}>
              <Descriptions.Item label={t("withdraw.status.title")}>
                <Badge status={color} text={statusText} />
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.requestDate")}>
                {ensureString({ text: reqDateFa ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.repayDate")}>
                {ensureString({ text: repayDateFa ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.trackingNumber")}>
                {ensureString({ text: trackingNumber ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.shaba")}>
                {ensureString({ text: details?.shaba ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.withdraws.details.settlementType")}>
                {ensureString({ text: settlementType ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              {details?.isPardakhtYar ? (
                <Descriptions.Item label={t("report.withdraws.details.gateway")}>
                  {ensureString({ text: details?.ipgType ?? "", fallbackValue: "-" })}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label={t("report.withdraws.details.code")}>
                {ensureString({ text: code ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              {details?.description ? (
                <Descriptions.Item label={t("report.withdraws.details.description")}>
                  {ensureString({ text: details?.description ?? "", fallbackValue: "-" })}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          )}
        </Col>
      </Row>
    </Drawer>
  );
};

export default WithdrawDetails;
